import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
import toast from 'react-hot-toast';

import { PrimaryBlackButton, PrimaryWhiteButton } from '../../../components/Button/Buttons';
import { Card, CardTitle, Hint, Input, Money, Scroll, Select, SimpleTable } from '../../../components/Layout/PagePieces';
import useFreshPasskey from '../../../hooks/auth/passkeys/useFreshPasskey';
import useCommissionRates, { CommissionPreview, CommissionRate, CommissionRates } from '../../../hooks/dashboard/profit/useCommissionRates';
import { formatNaira, tableFormatDateTimeNG } from '../../../utils/utils';

type Draft = { service_id: string; meter_class: string; rate_pct: string; cap: string; isNew: boolean };

const money = (v: string | number) => formatNaira(Number(v));
const pct = (v: string) => `${Number(v)}%`;
const meterLabel = (m: string) => (m === 'ANY' ? 'Any meter' : m === 'MD' ? 'MD meters' : 'Non-MD meters');

// VTpass commission rates (D13a) with the safeguards in pricing spec P9:
// rows are read-only until you choose Edit; every change is previewed (old →
// new, what it does to one purchase's profit), needs a reason, re-checks
// your passkey, and a big change needs you to tick that it matches VTpass's
// sheet and type the product's name. The backend refuses a change if the
// rate moved since you opened the page.
const CommissionRatesCard: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const { rates, setRates, fetchRates } = useCommissionRates();
  const { call, fresh } = useFreshPasskey();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [preview, setPreview] = useState<CommissionPreview | null>(null);
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchRates();
    // eslint-disable-next-line
  }, []);

  const current = (d: Draft) => rates?.current.find((r) => r.service_id === d.service_id && r.meter_class === d.meter_class);
  const expected = (d: Draft) => {
    const r = current(d);
    return { expected_rate_pct: r ? r.rate_pct : null, expected_cap: r ? r.cap : null };
  };
  const body = (d: Draft) => ({
    service_id: d.service_id.trim(),
    meter_class: d.meter_class,
    rate_pct: d.rate_pct,
    cap: d.cap === '' ? null : d.cap,
    ...expected(d),
  });

  const close = () => {
    setDraft(null);
    setPreview(null);
    setReason('');
    setChecked(false);
    setTyped('');
  };

  const edit = (r: CommissionRate) => {
    close();
    setDraft({ service_id: r.service_id, meter_class: r.meter_class, rate_pct: String(Number(r.rate_pct)), cap: r.cap ? String(Number(r.cap)) : '', isNew: false });
  };

  const review = async () => {
    if (!draft || draft.rate_pct === '' || !draft.service_id.trim()) return toast.error('Product and rate are needed');
    setBusy(true);
    try {
      setPreview(await call<CommissionPreview>({ url: 'api/v1/admin/commission-rates/preview', method: 'post', data: body(draft) }));
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!draft || !preview) return;
    setBusy(true);
    try {
      const data = await fresh<CommissionRates>({
        url: 'api/v1/admin/commission-rates',
        method: 'put',
        data: { ...body(draft), reason: reason.trim(), confirm_large_change: preview.large && checked },
      });
      setRates(data);
      toast.success(`${draft.service_id} saved. Unit economics uses it now.`);
      close();
      onSaved?.();
    } catch (err) {
      toast.error((err as Error).message);
      if (/changed since/.test((err as Error).message)) fetchRates();
    } finally {
      setBusy(false);
    }
  };

  const confirmed = !preview?.large || (checked && typed.trim() === draft?.service_id.trim());
  const canSave = preview != null && reason.trim().length >= 5 && confirmed && !busy;

  return (
    <Card>
      <CardTitle>VTpass commission rates</CardTitle>
      <Hint>
        What VTpass pays us per product. Used to check every purchase and for Unit economics; the real margin always comes
        from VTpass’s answer. Rates are locked: choose Edit, review the change, and confirm it with your passkey.
      </Hint>

      {draft && (
        <Editor>
          <h4>{draft.isNew ? 'Add a rate' : `Edit ${draft.service_id} (${meterLabel(draft.meter_class)})`}</h4>
          <Row>
            {draft.isNew && (
              <>
                <Input placeholder='Product (e.g. mtn)' value={draft.service_id} onChange={(e) => { setPreview(null); setDraft({ ...draft, service_id: e.target.value.trim() }); }} />
                <Select value={draft.meter_class} onChange={(e) => { setPreview(null); setDraft({ ...draft, meter_class: e.target.value }); }}>
                  <option value='ANY'>Any meter</option>
                  <option value='MD'>MD meters</option>
                  <option value='NON_MD'>Non-MD meters</option>
                </Select>
              </>
            )}
            <label>
              <span>Rate %</span>
              <Input type='number' step='0.01' value={draft.rate_pct} onChange={(e) => { setPreview(null); setDraft({ ...draft, rate_pct: e.target.value }); }} />
            </label>
            <label>
              <span>Cap ₦ (empty = none)</span>
              <Input type='number' value={draft.cap} onChange={(e) => { setPreview(null); setDraft({ ...draft, cap: e.target.value }); }} />
            </label>
          </Row>

          {!preview ? (
            <Row>
              <PrimaryBlackButton type='button' onClick={review} disabled={busy}>
                {busy ? 'Checking…' : 'Review change'}
              </PrimaryBlackButton>
              <PrimaryWhiteButton type='button' onClick={close}>
                Cancel
              </PrimaryWhiteButton>
            </Row>
          ) : (
            <Review>
              <p>
                <strong>{preview.service_id}</strong> ({meterLabel(preview.meter_class)}):{' '}
                {preview.current ? `${pct(preview.current.rate_pct)}${preview.current.cap ? `, cap ${money(preview.current.cap)}` : ''}` : 'no rate yet'} →{' '}
                <strong>
                  {pct(preview.proposed.rate_pct)}
                  {preview.proposed.cap ? `, cap ${money(preview.proposed.cap)}` : ''}
                </strong>
              </p>
              <SimpleTable>
                <thead>
                  <tr>
                    <th>One purchase of</th>
                    <th className='num'>Earns now</th>
                    <th className='num'>Would earn</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.impact.map((i) => (
                    <tr key={i.amount}>
                      <td>{money(i.amount)}</td>
                      <td className='num'>{i.before != null ? <Money className={Number(i.before) < 0 ? 'loss' : ''}>{money(i.before)}</Money> : '—'}</td>
                      <td className='num'>{i.after != null ? <Money className={Number(i.after) < 0 ? 'loss' : 'gain'}>{money(i.after)}</Money> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </SimpleTable>
              {preview.large && (
                <Warning>
                  <strong>This is a big change.</strong> {preview.warnings.join(' ')}
                  <label>
                    <input type='checkbox' checked={checked} onChange={(e) => setChecked(e.target.checked)} /> I’ve checked this
                    against VTpass’s commission sheet
                  </label>
                  <label>
                    <span>Type {preview.service_id} to confirm</span>
                    <Input value={typed} onChange={(e) => setTyped(e.target.value)} />
                  </label>
                </Warning>
              )}
              <label>
                <span>Why is it changing?</span>
                <Input placeholder='e.g. VTpass commission sheet of 2 Nov 2026' value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
              </label>
              <Row>
                <PrimaryBlackButton type='button' onClick={save} disabled={!canSave}>
                  {busy ? 'Saving…' : 'Confirm with passkey and save'}
                </PrimaryBlackButton>
                <PrimaryWhiteButton type='button' onClick={() => setPreview(null)}>
                  Back
                </PrimaryWhiteButton>
              </Row>
            </Review>
          )}
        </Editor>
      )}

      <Scroll>
        <SimpleTable>
          <thead>
            <tr>
              <th>Product</th>
              <th>Meter</th>
              <th className='num'>Rate</th>
              <th className='num'>Cap</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(rates?.current ?? []).map((r) => (
              <tr key={`${r.service_id}-${r.meter_class}`}>
                <td>{r.service_id}</td>
                <td>{r.meter_class === 'ANY' ? 'Any' : r.meter_class}</td>
                <td className='num'>{pct(r.rate_pct)}</td>
                <td className='num'>{r.cap ? money(r.cap) : '—'}</td>
                <td className='num'>
                  <LinkButton type='button' onClick={() => edit(r)}>
                    Edit
                  </LinkButton>
                </td>
              </tr>
            ))}
          </tbody>
        </SimpleTable>
      </Scroll>
      {!draft && (
        <LinkButton type='button' onClick={() => setDraft({ service_id: '', meter_class: 'ANY', rate_pct: '', cap: '', isNew: true })}>
          + Add a rate for another product or meter type
        </LinkButton>
      )}

      {!!rates?.history.filter((h) => h.changed_by !== 'seed').length && (
        <>
          <h4>Changes</h4>
          <Scroll>
            <SimpleTable>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Product</th>
                  <th className='num'>Rate</th>
                  <th className='num'>Cap</th>
                  <th>Why</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {rates.history
                  .filter((h) => h.changed_by !== 'seed')
                  .slice(0, 30)
                  .map((h, i) => (
                    <tr key={i}>
                      <td>{tableFormatDateTimeNG(h.effective_from)}</td>
                      <td>
                        {h.service_id}
                        {h.meter_class !== 'ANY' ? ` (${h.meter_class})` : ''}
                      </td>
                      <td className='num'>{pct(h.rate_pct)}</td>
                      <td className='num'>{h.cap ? money(h.cap) : '—'}</td>
                      <td>{h.reason ?? '—'}</td>
                      <td>{h.changed_by}</td>
                    </tr>
                  ))}
              </tbody>
            </SimpleTable>
          </Scroll>
        </>
      )}
    </Card>
  );
};

export default CommissionRatesCard;

const Editor = styled.div`
  margin: 12px 0;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  background: var(--panel);
  display: flex;
  flex-direction: column;
  gap: 10px;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
    color: var(--muted);
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;

  input {
    width: 150px;
  }
`;

const Review = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14px;

  p {
    margin: 0;
  }
`;

const Warning = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--red);
  font-size: 13px;

  label {
    flex-direction: row;
    align-items: center;
    gap: 6px;
    color: inherit;
  }
  label:last-of-type {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LinkButton = styled.button`
  border: 0;
  background: none;
  padding: 0;
  color: var(--ok);
  font-size: 13px;
  cursor: pointer;
  margin-top: 8px;
`;
